import Game from '../game';
import Ship, { ShipParams } from '../element/ship/ship';
import Weapon, { WeaponParams } from '../element/ship/weapon';
import Control, { ControlParams } from './control';
import RTSControls from './rtsControls';
import Menu from '../ui/menu/menu';

import { CoordsType, getAngleBy2XY, getDistanceBy2XY } from '../../utils/geometry';
import { QuadtreeItem } from 'quadtree-lib';


export interface ConstructionControlParams extends ControlParams {
  buildingParams: ShipParams | WeaponParams;
  buildingClass: 'Ship' | 'Weapon';
}

export default class ConstructionControl extends Control implements ConstructionControlParams {
  buildingParams!: ShipParams | WeaponParams;
  buildingClass!: 'Ship' | 'Weapon';

  coords!: CoordsType;
  temporaryCtx!: CanvasRenderingContext2D;
  virtualCursorCoords?: CoordsType; // used when selecting the weapon's angle

  constructor(params: ConstructionControlParams) {
    super(params);
    this.applyParams(params)

    const { x = 0, y = 0 } = Game.instance.cursor
    this.coords = { x, y };

    const canvas = document.createElement('canvas');
    canvas.width = (this.buildingParams?.spriteParams?.width || this.buildingParams.width) as number;
    canvas.height = (this.buildingParams?.spriteParams?.height || this.buildingParams.height) as number;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      this.temporaryCtx = ctx;
    }
  }

  getPointerCoords(e: MouseEvent, ignoreCamera = false): { x: number, y: number } {
    return RTSControls.prototype.getPointerCoords.apply(this, [e, ignoreCamera]);
  }

  onMouseMove = (e: MouseEvent): void => {
    e.preventDefault();
    this.updateCoords(e);
    this.drawIndicator();
  };

  onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    const button = ['left', 'middle', 'right'][e.button];
    switch (button) {
      case 'left':
        if (this.getConstructionValidity())
          this.beginConstruction();
        break;
    }
  };

  onMouseUp = (e: MouseEvent): void => {
    e.preventDefault();
    const button = ['left', 'middle', 'right'][e.button];
    switch (button) {
      case 'right':
        this.disable();
        break;
    }
  };

  onKeyDown = (e: KeyboardEvent): void => {
    if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space'].includes(e.code)) {
      return Game.instance.controlSchemes.rts.onKeyDown.apply(this, [e]);
    }
  };

  onKeyUp = (e: KeyboardEvent): void => {
    if (['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)) {
      return Game.instance.controlSchemes.rts.onKeyUp.apply(this, [e]);
    }
  };

  onMouseWheel = (e: WheelEvent): void => {
    return Game.instance.controlSchemes.rts.onMouseWheel.apply(this, [e]);
  };

  updateCoords(e: MouseEvent): void {
    const { x, y } = this.getPointerCoords(e);
    if (this.virtualCursorCoords) {
      const camera = Game.instance.scene.camera;
      this.virtualCursorCoords.x += e.movementX / (camera?.zoomRatio ?? 1);
      this.virtualCursorCoords.y += e.movementY / (camera?.zoomRatio ?? 1);

      Game.instance.cursor.lookAngle = getAngleBy2XY({ x1: this.coords.x, y1: this.coords.y, x2: this.virtualCursorCoords.x, y2: this.virtualCursorCoords.y });
      Game.instance.cursor.x = this.coords.x;
      Game.instance.cursor.y = this.coords.y;

    } else {
      this.coords.x = x;
      this.coords.y = y;
    }
  }

  drawIndicator(): void {
    const { sprite } = Game.instance.cursor
    const { temporaryCtx } = this;
    const { width: temporaryCanvasWidth, height: temporaryCanvasHeight } = temporaryCtx.canvas;

    /* Draw the shape colored in green or red */
    temporaryCtx.globalCompositeOperation = 'source-over';
    temporaryCtx.fillStyle = this.getConstructionValidity() ? 'green' : 'red';
    temporaryCtx.fillRect(0, 0, this.temporaryCtx.canvas.width, this.temporaryCtx.canvas.height);
    temporaryCtx.globalCompositeOperation = 'destination-in';
    const image = new Image();
    image.src = this.buildingParams?.spriteParams?.src as string;
    temporaryCtx.drawImage(
      image,
      0,
      0,
      temporaryCanvasWidth,
      temporaryCanvasHeight
    );

    sprite!.image = temporaryCtx.canvas;
    sprite!.offsetCenterX = 0;
    sprite!.offsetCenterY = 0;
    sprite!.width = temporaryCanvasWidth;
    sprite!.height = temporaryCanvasHeight;
    sprite!.ignoreScale = false;
  }

  getValidityByQuadTrees(selectablesInCollision: QuadtreeItem[]) {
    return selectablesInCollision?.length === 0;
  }

  getConstructionValidity(): boolean {
    const scene = Game.instance.scene;
    const cursor = Game.instance.cursor;
    const selectablesQuadtree = scene.quadtree.filter((item: QuadtreeItem) => scene.elements[item.id]?.selectable as boolean);
    const selectablesInCollision = selectablesQuadtree?.colliding({
      x: this.coords.x - (cursor?.sprite?.width ?? 0) / 2,
      y: this.coords.y - (cursor?.sprite?.height ?? 0) / 2,
      width: cursor.sprite?.width,
      height: cursor.sprite?.height,
    });

    if (this.buildingClass === 'Ship') {
      return selectablesInCollision?.length === 0;
    } else {
      return selectablesInCollision.length === 1 && selectablesInCollision[0].id === (this.buildingParams as WeaponParams)?.ship?.id;
    }
  }

  beginConstruction(): void {
    if (this.buildingClass == 'Weapon') {
      /* Placing a weapon pauses the game and allow the cursor to choose the desired angle for the new weapon */
      if (this.virtualCursorCoords) {
        this.commitConstruction()
      } else {
        this.virtualCursorCoords = {
          x: this.coords.x,
          y: this.coords.y,
        }
        Game.instance.paused = true; // force pause
      }
    } else if (this.buildingClass == 'Ship') {
      this.commitConstruction()
    }
  }

  commitConstruction(): void {
    const { cost = 0 } = this.buildingParams?.building ?? {};
    if (!Game.instance.player.payResources(cost)) { // not enough "minerals" :D
      this.disable()
      return;
    }

    if (this.buildingClass == 'Weapon') {
      const ship = (this.buildingParams as WeaponParams).ship;
      const angleFromShipCenter = Weapon.prototype.getRelativeAngle.apply({ ship }, [getAngleBy2XY({ x1: (ship?.x ?? 0), y1: (ship?.y ?? 0), x2: this.coords.x, y2: this.coords.y })]);
      const distanceFromShipCenter = getDistanceBy2XY({ x1: (ship?.x ?? 0), y1: (ship?.y ?? 0), x2: this.coords.x, y2: this.coords.y });

      const weapon = new Weapon({
        ...this.buildingParams,
        angleFromShipCenter,
        distanceFromShipCenter,
      });
      weapon.lookAngleRelativeDefault = weapon.getRelativeAngle(Game.instance.cursor?.lookAngle ?? 0);
      weapon.lookAngleRelative = weapon.lookAngleRelativeDefault;
      weapon.update(0); weapon.draw(); // to display the weapon even during the paused state
      Game.instance.paused = false; // resume game

    } else if (this.buildingClass == 'Ship') {
      new Ship({
        ...this.buildingParams,
        x: this.coords.x,
        y: this.coords.y,
      } as ShipParams).create();
    }

    this.disable();
  }

  enable(): ConstructionControl {
    super.enable();

    if (Number(this.buildingParams?.building?.cost) > (Game.instance.player?.resources ?? 0)) // not enough "minerals"
      return this.disable();

    Game.instance.controlSchemes.rts.disable();
    this.drawIndicator();
    Menu.closeAll();

    return this;
  }

  disable(): ConstructionControl {
    super.disable();
    Game.instance.cursor.setSprite();
    Game.instance.cursor.lookAngle = 90; // reset cursor's angle
    Game.instance.controlSchemes.rts.enable();
    return this;
  }
}
